import {Box, createStyles, Text} from '@mantine/core';
import React, {useState} from 'react';

import {
  CONJUGATION_SITES_UNSUPPORTED_METHOD_ERROR,
  ConjugationSite,
} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteConstants';
import {
  conjugationSiteKey,
  formatConjugationSite,
} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteUtils';
import {AntibodyDomain} from 'molbio/adcs/conjugation-sites/antibodyDomainsQuery';
import {ConjugationSiteChips} from 'molbio/adcs/registration-table/AdcConjugationSitesDisplay';
import useAdcConjugationSitesCell from 'molbio/adcs/registration-table/useAdcConjugationSitesCell';
import getTestProps from 'test/lib/cypress/getTestProps';
import MultiSelectSearchableDropdown from 'ui-library/dropdowns/MultiSelectSearchableDropdown';
import {OptionData} from 'ui-library/menus/MenuConstants';

export const SELECT_PROTEIN_FIRST_MESSAGE = 'Select a protein first';
export const SELECT_CONJUGATION_METHOD_FIRST_MESSAGE = 'Select a conjugation method first';

const CONJUGATION_SITES_DROPDOWN_LABEL = 'Conjugation sites';

const useStyles = createStyles(() => ({
  dropdown: {
    border: 'none',
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
}));

interface ConjugationSitesPlaceholderProps {
  sites: Array<ConjugationSite>;
  domains: ReadonlyArray<AntibodyDomain> | null;
  message: string;
}

/** Shows the selected sites, or a hint explaining why no sites can be chosen yet. */
function ConjugationSitesPlaceholder({
  sites,
  domains,
  message,
}: ConjugationSitesPlaceholderProps): JSX.Element {
  if (sites.length > 0) {
    return <ConjugationSiteChips sites={sites} domains={domains} />;
  }
  return (
    <Text size="sm" color="dimmed">
      {message}
    </Text>
  );
}

interface ConjugationSitesDropdownProps {
  candidates: Array<ConjugationSite>;
  sites: Array<ConjugationSite>;
  domains: ReadonlyArray<AntibodyDomain> | null;
  onOpenDropdown?: (() => void) | null;
  updateSites: (newSites: Array<ConjugationSite>) => void;
}

function ConjugationSitesDropdown({
  candidates,
  sites,
  domains,
  onOpenDropdown,
  updateSites,
}: ConjugationSitesDropdownProps): JSX.Element {
  const {classes} = useStyles();
  const [isOpen, setOpen] = useState(false);

  // Selected sites that are no longer candidates stay listed so they can be seen and removed; the column's
  // custom eval flags them.
  const siteByKey = new Map([...candidates, ...sites].map((site) => [conjugationSiteKey(site), site]));
  const optionDatas: Array<OptionData> = [...siteByKey].map(([key, site]) => ({
    name: formatConjugationSite(site, domains),
    value: key,
  }));

  function handleToggle(newIsOpen: boolean): void {
    if (newIsOpen) {
      onOpenDropdown?.();
    }
    setOpen(newIsOpen);
  }

  return (
    <MultiSelectSearchableDropdown
      ariaLabel={CONJUGATION_SITES_DROPDOWN_LABEL}
      buttonClassName={classes.dropdown}
      isOpen={isOpen}
      noneSelectedMessage=""
      onSelect={(keys) => {
        updateSites(keys.flatMap((key) => siteByKey.get(key) ?? []));
      }}
      onToggle={handleToggle}
      optionDatas={optionDatas}
      renderSelectedValues={() => (
        <ConjugationSiteChips
          sites={sites}
          domains={domains}
          // The dropdown's menu lists every selected site, so the toggle needs no expanded list of its own.
          isExpandable={false}
          onRemove={(removedSite) => {
            updateSites(sites.filter((site) => conjugationSiteKey(site) !== conjugationSiteKey(removedSite)));
          }}
        />
      )}
      selectedValues={sites.map((site) => conjugationSiteKey(site))}
    />
  );
}

interface AdcConjugationSitesCellProps {
  /** The cell's selected sites. */
  sites: Array<ConjugationSite>;
  /** Called when the dropdown opens, so the table can select the cell. */
  onOpenDropdown?: (() => void) | null;
  /** Called with the cell's new text when the selection changes. */
  onUpdate: (text: string) => void;
}

/**
 * Editable Conjugation sites cell in an ADC registration table. Lists the candidate sites for the row's
 * Protein and Conjugation method in a multi-select dropdown (see useAdcConjugationSitesCell), and shows a hint
 * instead until both are chosen. Must render inside a table row (see RowModelProvider).
 */
export default function AdcConjugationSitesCell({
  sites,
  onOpenDropdown,
  onUpdate,
}: AdcConjugationSitesCellProps): JSX.Element {
  const {antibodyId, conjugationMethod, candidates, domains, updateSites} = useAdcConjugationSitesCell(
    sites,
    onUpdate
  );

  let content: JSX.Element | null;
  if (antibodyId == null) {
    content = (
      <ConjugationSitesPlaceholder sites={sites} domains={domains} message={SELECT_PROTEIN_FIRST_MESSAGE} />
    );
  } else if (conjugationMethod == null) {
    content = (
      <ConjugationSitesPlaceholder
        sites={sites}
        domains={domains}
        message={SELECT_CONJUGATION_METHOD_FIRST_MESSAGE}
      />
    );
  } else if (candidates == null) {
    content = <ConjugationSiteChips sites={sites} domains={domains} />;
  } else if ('error' in candidates) {
    content = <ConjugationSitesPlaceholder sites={sites} domains={domains} message={candidates.error} />;
  } else if (!candidates.supported) {
    content = (
      <ConjugationSitesPlaceholder
        sites={sites}
        domains={domains}
        message={CONJUGATION_SITES_UNSUPPORTED_METHOD_ERROR}
      />
    );
  } else {
    content = (
      <ConjugationSitesDropdown
        candidates={candidates.candidates}
        sites={sites}
        domains={domains}
        onOpenDropdown={onOpenDropdown}
        updateSites={updateSites}
      />
    );
  }
  return <Box {...getTestProps({component: 'AdcConjugationSitesCell', element: 'root'})}>{content}</Box>;
}
